import React from 'react';
import { generatePath, useLocation, useRouteMatch } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';
import qs from 'query-string';

import {
  constants,
  EventProvider,
  extractEventMarketTypes,
  isEventOffline,
  isHidden,
  isSuspended,
  isVisible,
  useApplicationState,
  useBetslipState,
  useEventState,
  usePreferences,
  useTournamentState,
} from '@atlas/core';
import { EventLink } from '@atlas/routing';
import {
  EventDetailedScore,
  EventLiveParticipants,
  EventMarketTypeCompactGrid,
  FavoriteButton,
  Icon,
  mapEventMarketTypes,
  MarketPlaceholder,
  MarketTypeContainer,
  TabContent,
  TabNavigator,
  TournamentEventsPlaceholder,
  useEventViewHandlers,
  useTabNavigator,
} from '@atlas/ui';

import { SPORT_IDS_WITH_DETAILED_SCORE } from 'common/constants';
import EmptyOutcomesRow from 'common/Widgets/Tournament/Tournament/Events/Event/EmptyOutcomesRow';
import EventPeriodInfo from 'common/Widgets/Tournament/Tournament/Events/Event/EventPeriodInfo';
import ProfitIcons from 'components/Pages/ProfitIcons';

const {
  STATUS: { ACTIVE, SUSPENDED },
} = constants;

export const outcomeNameParser = outcome => {
  if (!outcome.specifiers) {
    return outcome.shortName;
  }
  if (outcome.name && outcome.specifiers) {
    return `${outcome.shortName}(${outcome.specifiers})`;
  }
  return outcome.specifiers;
};

const activeOrSuspendedMarketExists = marketTypes =>
  marketTypes.some(mt => {
    const getActiveOrSuspededMarket = () =>
      Object.values(mt.markets).find(m => m.status === ACTIVE || m.status === SUSPENDED);

    return mt.markets && getActiveOrSuspededMarket();
  });

const ScrollEventHeader = props => {
  const { eventId, eventLinkPostfix = '', hideButton, participants } = props;

  const { path } = useRouteMatch();
  const event = useEventState();

  const { categoryUrlName, noneMargin, oddsBust, sportUrlName, tournamentUrlName } = event;

  const toUrl = generatePath(path, {
    categoryName: categoryUrlName,
    sportName: sportUrlName,
    tournamentName: tournamentUrlName,
  });

  const { getIsEventFavorite, selectFavoriteEvent } = usePreferences();
  const applicationState = useApplicationState();

  const { authenticated } = applicationState;
  const [isActive, setActive] = useTabNavigator();

  const onClickHandler = React.useCallback(() => {
    setActive(!isActive);
  }, [isActive, setActive]);

  const arrowName = isActive ? 'arrow-up' : 'arrow-down';

  const isFavorite = getIsEventFavorite({ eventId: event.id });

  const setFavoriteEventHandler = React.useCallback(() => {
    selectFavoriteEvent({ eventId: event.id });
  }, [selectFavoriteEvent, event.id]);

  const LiveParticipantsComponent = React.useMemo(
    () =>
      SPORT_IDS_WITH_DETAILED_SCORE.includes(event.sportId) ? (
        <EventDetailedScore
          participants={participants}
          score={event.info.score}
          stats={event.info.stats}
        />
      ) : (
        <EventLiveParticipants
          participants={participants}
          score={event.info.score}
          variant="grid"
        />
      ),
    [event.info, participants, event.sportId],
  );

  return (
    <div className="event-header container">
      <div className="event-period__info">
        <div className="event-status-bar">
          <EventPeriodInfo scroll />
        </div>

        <div className="event-period-main">
          <ProfitIcons noneMargin={noneMargin} oddsBust={oddsBust} />
          <EventLink
            className="event-period__link"
            customLocation={toUrl}
            dataTestId="event"
            dataTestItemId={eventId}
            eventId={`${eventId}${eventLinkPostfix}`}
          >
            {LiveParticipantsComponent}
          </EventLink>
          <div className="event-item-icon-controls">
            {authenticated && event.sportService !== constants.SPORT_SERVICE.ARCHIVED && (
              <div className="font-icon-container with-border">
                <FavoriteButton
                  active={isFavorite}
                  className="icon-box box-width"
                  onClick={setFavoriteEventHandler}
                />
              </div>
            )}

            {!hideButton && (
              <div className="font-icon-container" onClick={onClickHandler}>
                <Icon className={arrowName} name={arrowName} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const MarketTypesContainer = ({ children, eventId, marketTypes }) => {
  if (activeOrSuspendedMarketExists(marketTypes)) {
    return children;
  }

  return (
    <div className="tournament-scroll-outcomes-row">
      <EmptyOutcomesRow eventId={eventId} />
    </div>
  );
};

const ScrollEvent = props => {
  const {
    className,
    eventLinkPostfix,
    hasSportFormMainTypes,
    marketTypeClassName,
    marketTypesPeriodMap,
    onEventClick,
    outcomeClassName,
    suspended,
  } = props;

  const event = useEventState();

  const isEventSuspended = suspended || isSuspended(event);
  const handleClick = useEventViewHandlers(event, onEventClick);
  const eventClassName = cx('event event_default', className);

  const multilineComponents = React.useMemo(
    /*
      We need to skip first item in marketTypes period map
      It is always fulltime
    */
    () =>
      mapEventMarketTypes(marketTypesPeriodMap.slice(1), event.marketTypes, periodMarketTypes => {
        /*
          Using first market type for main market type instructions
          We need some general information (periodTypeId) about each period
          Passed as props for special component EventPeriodInfoComponent
        */
        const [marketTypeInstruction] = periodMarketTypes;

        return (
          <EventMarketTypeCompactGrid
            className="event__market-types"
            hasSportFormMainTypes={hasSportFormMainTypes}
            key={marketTypeInstruction.periodTypeId}
            marketTypeClassName={marketTypeClassName}
            marketTypes={periodMarketTypes}
            outcomeClassName={outcomeClassName}
            outcomeNameParser={outcomeNameParser}
            suspended={isEventSuspended}
          />
        );
      }),
    [
      event.marketTypes,
      marketTypesPeriodMap,
      hasSportFormMainTypes,
      marketTypeClassName,
      outcomeClassName,
      isEventSuspended,
    ],
  );

  if (
    isHidden(event) ||
    (isEventSuspended && event.sportService === constants.SPORT_SERVICE.ARCHIVED)
  ) {
    return null;
  }

  const isMultilineEmpty = multilineComponents.length < 1;
  const [fulltimeMarketTypesInstructions] = marketTypesPeriodMap;
  const fulltimeMainMarketTypes = extractEventMarketTypes(
    event.marketTypes,
    fulltimeMarketTypesInstructions,
  );

  return (
    <div className={eventClassName} data-eid={event.id} onClick={handleClick}>
      <ScrollEventHeader
        eventId={event.id}
        eventLinkPostfix={eventLinkPostfix}
        hideButton={isMultilineEmpty}
        marketTypesPeriodMap={marketTypesPeriodMap}
        participants={event.participants}
        score={event.info.score}
        startTime={event.startTime}
      />
      <MarketTypesContainer eventId={event.id} marketTypes={fulltimeMainMarketTypes}>
        <div className="event-content">
          <EventMarketTypeCompactGrid
            className="event__market-types"
            hasSportFormMainTypes={hasSportFormMainTypes}
            marketTypeClassName={marketTypeClassName}
            marketTypes={fulltimeMainMarketTypes}
            outcomeClassName={outcomeClassName}
            outcomeNameParser={outcomeNameParser}
            suspended={isEventSuspended}
          />
        </div>
        <TabContent
          className="event-content"
          // Event content should not be displayed for empty multiline
          // Multiline can be expanded by default
          hide={isMultilineEmpty}
          index
        >
          {multilineComponents}
        </TabContent>
      </MarketTypesContainer>
    </div>
  );
};

const TournamentScrollEvents = props => {
  const { search } = useLocation();
  const eventLinkPostfix = search.includes('tab=') ? `&tab=${qs.parse(search).tab}` : '';

  const {
    eventClassName,
    eventProvider: TournamentEventProvider = EventProvider,
    events,
    feeds,
    hasSportFormMainTypes,
    marketTypeClassName,
    marketTypesPeriodMap,
    onEventClick,
    outcomeClassName,
  } = props;

  return (
    Object.values(events)
      // Show only visible default events
      .filter(event => isVisible(event) && !event.outright)
      // ascending sorting order
      .sort((a, b) => a.startTime - b.startTime)
      .map((event, index) => {
        const { source, sportId, sportService } = event;
        const suspended = isEventOffline({ feeds, source, sportId, sportService });

        return (
          <TournamentEventProvider event={event} key={event.id}>
            <TabNavigator index={index === 0}>
              <ScrollEvent
                className={eventClassName}
                eventLinkPostfix={eventLinkPostfix}
                hasSportFormMainTypes={hasSportFormMainTypes}
                marketTypeClassName={marketTypeClassName}
                marketTypesPeriodMap={marketTypesPeriodMap}
                onEventClick={onEventClick}
                outcomeClassName={outcomeClassName}
                suspended={suspended}
              />
            </TabNavigator>
          </TournamentEventProvider>
        );
      })
  );
};

TournamentScrollEvents.propTypes = {
  eventClassName: PropTypes.string,
  eventProvider: PropTypes.elementType,
  events: PropTypes.arrayOf(PropTypes.object).isRequired,
  feeds: PropTypes.object.isRequired,
  hasSportFormMainTypes: PropTypes.bool,
  marketTypeClassName: PropTypes.string,
  marketTypesPeriodMap: PropTypes.array.isRequired,
  onEventClick: PropTypes.func.isRequired,
  outcomeClassName: PropTypes.string,
};

function renderComptactMarketTypePlaceholder(marketType) {
  const { outcomeTypes, parameterized } = marketType;

  const className = cx('placeholder col', { parameterized });

  return (
    <MarketTypeContainer
      className={className}
      hidePriority={marketType.hidePriority}
      key={marketType.id}
      outcomeTypes={outcomeTypes}
    >
      <div className="market-header">{marketType.name}</div>
      <div className="market">
        <MarketPlaceholder outcomeTypes={marketType.outcomeTypes} />
      </div>
    </MarketTypeContainer>
  );
}

function renderCompactEventPlaceholder(marketPlaceholder, index) {
  return (
    <div className="event event_default scroll event_placeholder" key={index}>
      <div className="event-header sport">
        <div className="event-period__info">
          <div className="event-period-main" />
        </div>
      </div>

      <div className="event-content">
        <div className="event-period row">{marketPlaceholder}</div>
      </div>
    </div>
  );
}

const TournamentScrollEventsPlaceholder = props => {
  const { count, marketTypesPeriodMap } = props;

  return (
    <TournamentEventsPlaceholder
      count={count}
      marketTypesPeriodMap={marketTypesPeriodMap}
      renderEventPlaceholder={renderCompactEventPlaceholder}
      renderMarketTypePlaceholder={renderComptactMarketTypePlaceholder}
    />
  );
};

export const TournamentScrollGrid = props => {
  const {
    className,
    eventClassName = 'scroll',
    eventProvider,
    hasSportFormMainTypes,
    marketTypeClassName,
    marketTypesPeriodMap,
    onEventClick,
    outcomeClassName = 'primary',
  } = props;

  const {
    events,
    id,
    initialized,
    liveCount = 0,
    loading,
    prematchCount = 0,
  } = useTournamentState();

  const { settings } = useBetslipState();
  /*
    Validating tournament state
  */
  const TournamentEventsView =
    loading && !initialized ? TournamentScrollEventsPlaceholder : TournamentScrollEvents;

  const tournamentClassName = cx('tournament', className);

  return (
    <div className={tournamentClassName} data-tid={id}>
      <TournamentEventsView
        count={prematchCount + liveCount}
        eventClassName={eventClassName}
        eventProvider={eventProvider}
        events={events}
        feeds={settings.feeds}
        hasSportFormMainTypes={hasSportFormMainTypes}
        marketTypeClassName={marketTypeClassName}
        marketTypesPeriodMap={marketTypesPeriodMap}
        onEventClick={onEventClick}
        outcomeClassName={outcomeClassName}
      />
    </div>
  );
};

TournamentScrollGrid.propTypes = {
  className: PropTypes.string,
  eventClassName: PropTypes.string,
  eventProvider: PropTypes.elementType,
  hasSportFormMainTypes: PropTypes.bool,
  marketTypeClassName: PropTypes.string,
  marketTypesPeriodMap: PropTypes.array.isRequired,
  onEventClick: PropTypes.func.isRequired,
  outcomeClassName: PropTypes.string,
};
